// C:\MonProjet\BBW_DELEGUER\preprod\babyworld-backoffice\babyworld-backoffice\src\pages\rapports\columns.js

import React from 'react';
import { DeleteOutlined, EditOutlined, StarFilled, StopOutlined, RocketOutlined } from "@ant-design/icons";
import { Modal, Button, Popconfirm, message, Tooltip, Typography, Tag, Space } from "antd";
import { useDispatch } from "react-redux";
import { deleteRapport } from "../../redux/slices/rapports/rapportsThunk";
import dayjs from 'dayjs';

const ActionColumn = ({ record, onEdit }) => {
  const dispatch = useDispatch();

  const handleDelete = (rapportId) => {
    dispatch(deleteRapport(rapportId))
      .unwrap()
      .then(() => {
        message.success("Rapport supprimé.");
      })
      .catch((error) => {
        message.error(`Erreur: ${error}`);
      });
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
      <Button 
        className="btn-details" 
        onClick={() => window.open(`/reportdetails/${record.id}`, '_blank')}
      >
        Détails
      </Button>

      <Tooltip title="Modifier la date">
        <EditOutlined 
            style={{ color: '#1890ff', fontSize: '18px', cursor: 'pointer', opacity: 0.8 }} 
            onClick={() => onEdit(record)}
        />
      </Tooltip>

      <Popconfirm
        title="Supprimer ce rapport ?"
        onConfirm={() => handleDelete(record.id)}
        okText="Oui" cancelText="Non"
      >
        <DeleteOutlined style={{ color: '#ff4d4f', fontSize: '18px', cursor: 'pointer', opacity: 0.8 }} />
      </Popconfirm>
    </div>
  );
};

export const columns = (onEdit, onOpenHistory) => {
  return [
    {
      title: "Nom",
      dataIndex: "person",
      key: "name",
      width: 270,
      render: (person) => {
        if (!person) return 'N/A';

        // ⚡ Détection exacte des catégories
        const cat = person.relationshipCategory || person.prescriptionProfile || (person.isPartner ? 'PARTNER_ACTIVE' : 'STANDARD');
        const raw = String(cat || '').toUpperCase().trim();

        const isRefractory = raw === 'REFRACTORY' || raw.includes('REFRACT') || raw.includes('RÉCEPTIF') || raw.includes('RECEPTIF');
        const isAtRisk = raw === 'PARTNER_AT_RISK' || raw.includes('VIGILANCE');
        const isKeyPrescriber = raw === 'KEY_PRESCRIBER' || raw.includes('A+') || raw.includes('PRESCRIPTEUR');
        const isPartnerActive = !isAtRisk && !isRefractory && !isKeyPrescriber && (
            raw === 'PARTNER_ACTIVE' || person.isPartner === true || person.isPartner === 'true' || person.isPartner === 1
        );

        const fullName = person.type === 2 && person.pharmacien?.pharmacyName 
            ? `${person.pharmacien.pharmacyName} (${person.firstname} ${person.lastname})`
            : `${person.firstname || ''} ${person.lastname || ''}`.trim();
        
        // ⚡ Couleur exacte du texte du médecin
        let linkColor = '#1890ff';
        if (isRefractory) linkColor = '#cf1322';       // Rouge vif
        else if (isAtRisk) linkColor = '#d46b08';      // Orange ambré
        else if (isKeyPrescriber) linkColor = '#389e0d'; // Vert
        else if (isPartnerActive) linkColor = '#856404'; // Or/Marron foncé

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
            
            {/* ⭐ Étoile dorée pour Partenaire Stratégique [B+] */}
            {isPartnerActive && (
              <Tooltip title={`⭐ Partenaire Stratégique [B+] : ${person.partnerNotes || ''}`}>
                <StarFilled style={{ color: '#fadb14', fontSize: '15px' }} />
              </Tooltip>
            )}

            {/* ⭐ Étoile orange pour Partenaire Sous Vigilance [A-] */}
            {isAtRisk && (
              <Tooltip title={`⚠️ Partenaire Sous Vigilance [A-] : ${person.partnerNotes || ''}`}>
                <StarFilled style={{ color: '#fa8c16', fontSize: '15px' }} />
              </Tooltip>
            )}
            
            {/* ⚡ LIEN DU MÉDECIN : EN GRAS ('bold') AVEC COULEUR ATTRIBUÉE */}
            <a 
              onClick={() => onOpenHistory && onOpenHistory(person.id)} 
              style={{ 
                fontWeight: 'bold', // 👈 TOUJOURS EN GRAS
                fontSize: '13px',
                color: linkColor,
                textDecoration: (isPartnerActive || isAtRisk) ? 'underline' : 'none',
                cursor: 'pointer'
              }}
            >
              {fullName}
            </a>

            {/* ⚡ BADGE PARTENAIRE STRATÉGIQUE */}
            {isPartnerActive && (
              <Tag color="gold" style={{ fontSize: '10px', fontWeight: 'bold', padding: '0 4px', lineHeight: '18px', margin: 0 }}>
                STRATEGIQUE [B+]
              </Tag>
            )}

            {/* ⚡ BADGE PARTENAIRE SOUS VIGILANCE */}
            {isAtRisk && (
              <Tag color="warning" style={{ fontSize: '10px', fontWeight: 'bold', padding: '0 4px', lineHeight: '18px', margin: 0 }}>
                ⚠️ VIGILANCE [A-]
              </Tag>
            )}

            {/* ⚡ BADGE PRESCRIPTEUR CLÉ */}
            {isKeyPrescriber && (
              <Tag color="success" style={{ fontSize: '10px', fontWeight: 'bold', padding: '0 4px', lineHeight: '18px', margin: 0 }}>
                🚀 PRESCRIPTEUR [B]
              </Tag>
            )}

            {/* ⚡ BADGE NON RÉCEPTIF */}
            {isRefractory && (
              <Tag color="error" style={{ fontSize: '10px', fontWeight: 'bold', padding: '0 4px', lineHeight: '18px', margin: 0 }}>
                <StopOutlined /> NON RÉCEPTIF [C-]
              </Tag>
            )}
          </div>
        );
      },
    },
    { 
      title: "Spécialité", 
      dataIndex: "person", 
      key: "specialty", 
      render: (person) => {
        if (person?.type === 2) return <span style={{ fontWeight: "bold", color: "#52c41a" }}>PHAR</span>;
        if (person?.type === 3) return <span style={{ fontWeight: "bold", color: "#faad14" }}>GROS</span>;
        return person?.doctor?.specialty?.abreviation || person?.doctor?.specialty?.name || "N/A";
      }
    },
    {
      title: "Délégué",
      dataIndex: "delegate",
      key: "delegate",
      render: (delegate) => delegate ? `${delegate.firstname} ${delegate.lastname}`.toUpperCase() : 'N/A'
    },
    { title: "Gouvernorat", dataIndex: "government", key: "government", render: (gov) => gov?.name || 'unknown' },
    { title: "Région", dataIndex: "region", key: "region", render: (region) => region?.name || 'N/A' },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (dateString) => dateString ? dayjs(dateString).format('YYYY-MM-DD') : 'N/A',
    },
    {
      title: "Note",
      dataIndex: "note",
      key: "note",
      width: 250,
      render: (note, record) => {
        const isUnfinished = 
          record.status === 'VISITE_NON_TERMINEE' || 
          record.status === 'VISITE_NON_ABOUTIE' ||
          (note && (note.includes('[VISITE NON TERMINÉE]') || note.includes('[VISITE NON ABOUTIE]')));

        if (!note || note.trim() === "" || note.trim() === ".") {
          return <span style={{ color: '#bfbfbf', fontStyle: 'italic' }}>.</span>;
        }

        const showFullNote = () => {
          Modal.info({
            title: isUnfinished ? '⚠️ Motif de visite non aboutie' : 'Commentaire complet',
            content: (
              <div style={{ marginTop: '20px', fontSize: '15px', lineHeight: '1.6' }}>
                {note}
              </div>
            ),
            width: 600,
            okText: 'Fermer',
            maskClosable: true, // ⚡ Corrigé ici
          });
        };

        return (
          <div style={{ display: 'flex', flexDirection: 'column', width: '230px' }}>
            {isUnfinished && (
              <Tag color="volcano" style={{ fontWeight: 'bold', fontSize: '11px', width: 'fit-content', marginBottom: 4 }}>
                ⚠️ Visite non aboutie
              </Tag>
            )}

            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Typography.Text ellipsis={true} style={{ flex: 1, color: isUnfinished ? '#d4380d' : 'inherit' }}>
                {note.replace(/\[VISITE NON TERMINÉE\]|\[VISITE NON ABOUTIE\]/gi, '').trim()}
              </Typography.Text>

              {note.length > 20 && (
                <a onClick={showFullNote} style={{ marginLeft: '8px', fontWeight: 'bold', color: '#1890ff', whiteSpace: 'nowrap' }}>
                  Lire plus
                </a>
              )}
            </div>
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      fixed: 'right',
      render: (record) => <ActionColumn record={record} onEdit={onEdit} />,
    }
  ];
};